import React from 'react';
import { Tooltip, theme } from 'antd';
import { ShoppingOutlined } from '@ant-design/icons';

interface OrderProductsCellProps {
    lines?: any[];
    /** Maximum number of products shown before collapsing into "+N więcej" */
    maxVisible?: number;
    /** `table` = compact rows for the desktop table, `card` = boxed block for mobile cards */
    variant?: 'table' | 'card';
}

interface GroupedLine {
    key: string;
    /** Name shown in the compact list (shortName -> product name -> offer title) */
    name: string;
    /** Full catalog name when it differs from the displayed short name */
    catalogName?: string;
    /** Original offer title stored on the order line, when it differs from the displayed name */
    offerName?: string;
    sku?: string;
    quantity: number;
}

/**
 * Groups order lines by product so the same product bought in several batches
 * (FEFO splits one line per batch) is displayed once with a summed quantity.
 *
 * Display name priority:
 * 1. `product.shortName` - custom short label maintained in the ERP,
 * 2. `product.name` - catalog name,
 * 3. `line.name` - original offer title (e.g. from Allegro).
 */
export function groupOrderLinesForDisplay(lines?: any[]): GroupedLine[] {
    if (!lines || lines.length === 0) return [];

    // Shipping pseudo-lines are not products - skip them when real products exist.
    const productLines = lines.filter(
        (line) => !line.isShipping && line.sku !== 'WYSYŁKA' && line.name !== 'WYSYŁKA'
    );
    const source = productLines.length > 0 ? productLines : lines;

    const grouped = new Map<string, GroupedLine>();

    for (const line of source) {
        const key = line.productId || line.sku || line.name;
        if (!key) continue;

        const quantity = Number(line.quantity || 0);
        const existing = grouped.get(key);

        if (existing) {
            existing.quantity += quantity;
        } else {
            const shortName = line.product?.shortName?.trim();
            const catalogName = line.product?.name?.trim();
            const offerName = line.name?.trim();
            const displayName = shortName || catalogName || offerName || line.sku || 'Produkt';

            grouped.set(key, {
                key,
                name: displayName,
                catalogName: catalogName && catalogName !== displayName ? catalogName : undefined,
                offerName: offerName && offerName !== displayName && offerName !== catalogName ? offerName : undefined,
                sku: line.product?.sku || line.sku,
                quantity,
            });
        }
    }

    return Array.from(grouped.values());
}

export default function OrderProductsCell({
    lines,
    maxVisible = 2,
    variant = 'table',
}: OrderProductsCellProps) {
    const { token } = theme.useToken();
    const items = groupOrderLinesForDisplay(lines);

    if (items.length === 0) {
        return (
            <span style={{ fontSize: 12, color: token.colorTextDescription }}>
                Brak pozycji
            </span>
        );
    }

    const visible = items.slice(0, maxVisible);
    const hidden = items.slice(maxVisible);
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    const quantityBadge = (quantity: number) => (
        <span
            style={{
                flexShrink: 0,
                minWidth: 26,
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: '16px',
                color: token.colorPrimary,
                background: token.colorPrimaryBg,
                borderRadius: 4,
                padding: '0 4px',
            }}
        >
            {quantity}×
        </span>
    );

    const nameTooltip = (item: GroupedLine) => (
        <div style={{ maxWidth: 420 }}>
            <div style={{ fontWeight: 600 }}>{item.name}</div>
            {item.catalogName && (
                <div style={{ fontSize: 12, marginTop: 2 }}>
                    Nazwa katalogowa: {item.catalogName}
                </div>
            )}
            {item.offerName && (
                <div style={{ fontSize: 12, marginTop: 2 }}>
                    Oferta: {item.offerName}
                </div>
            )}
            {item.sku && (
                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.75 }}>
                    SKU: {item.sku}
                </div>
            )}
        </div>
    );

    const nameTitle = (item: GroupedLine) =>
        [
            item.name,
            item.catalogName ? `Katalog: ${item.catalogName}` : null,
            item.offerName ? `Oferta: ${item.offerName}` : null,
            item.sku ? `SKU: ${item.sku}` : null,
        ]
            .filter(Boolean)
            .join(' • ');

    const productName = (item: GroupedLine) => {
        const text = (
            <span
                title={variant === 'card' ? nameTitle(item) : undefined}
                style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                    color: token.colorText,
                }}
            >
                {item.name}
            </span>
        );

        if (variant === 'table') {
            return (
                <Tooltip title={nameTooltip(item)} mouseEnterDelay={0.35} placement="topLeft">
                    {text}
                </Tooltip>
            );
        }

        return text;
    };

    const moreTooltip = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {items.map((item) => (
                <div key={`all-${item.key}`} style={{ fontSize: 12 }}>
                    <strong>{item.quantity}×</strong> {item.name}
                    {item.sku && <span style={{ opacity: 0.7 }}> ({item.sku})</span>}
                </div>
            ))}
        </div>
    );

    if (variant === 'card') {
        return (
            <div
                style={{
                    marginBottom: 8,
                    padding: '7px 9px',
                    borderRadius: 6,
                    background: token.colorFillQuaternary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                            color: token.colorTextDescription,
                        }}
                    >
                        <ShoppingOutlined style={{ fontSize: 10 }} />
                        Produkty ({items.length})
                    </span>
                    <span style={{ fontSize: 10, color: token.colorTextDescription }}>
                        {totalQuantity} szt.
                    </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {visible.map((item) => (
                        <div
                            key={item.key}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                        >
                            {quantityBadge(item.quantity)}
                            {productName(item)}
                        </div>
                    ))}
                    {hidden.length > 0 && (
                        <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                            +{hidden.length} więcej
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '100%',
                minWidth: 0,
            }}
        >
            {visible.map((item) => (
                <div
                    key={item.key}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                >
                    {quantityBadge(item.quantity)}
                    {productName(item)}
                </div>
            ))}
            {hidden.length > 0 && (
                <Tooltip title={moreTooltip} placement="right">
                    <span
                        style={{
                            fontSize: 11,
                            color: token.colorPrimary,
                            cursor: 'help',
                            width: 'fit-content',
                        }}
                    >
                        +{hidden.length} więcej
                    </span>
                </Tooltip>
            )}
        </div>
    );
}
